import {Box, HStack, Text, VStack} from '@chakra-ui/layout';
import {Grid, GridItem, Link, Skeleton, Tooltip} from '@chakra-ui/react';
import {keyframes} from '@emotion/react';
import {PanierListe} from './controllers/PanierListe';
import {Trans, useTranslation} from 'react-i18next';
import {PanierHeader} from './PanierFound/PanierHeader';
import {PanierContent} from './PanierFound/PanierContent';
import {PanierRecapitulatif} from './PanierRecapitulatif/PanierRecapitulatif';
import {HaliRenderMany, useHalCollection, useHalState} from '@synako/haligator';
import {ulid} from 'ulid';
import {differenceInDays, formatDistanceToNow} from 'date-fns';
import {guessTitreAdresseDeLivraison, usePanierCommands} from 'ff-common';
import {Navigable, isLinkNotFound} from '@synako/halitrek';
import {PanierDesc} from 'api-types/ffconnect-hal';
import {useClientRoute} from '@/hooks/auth/useClientRoute';
import {routes} from 'ff-common';
import {useNavigate} from 'react-router-dom';
import {useSimpleTextToast} from '@/hooks/useSimpleTextToast';
import {useDefaultOnError} from '@/hooks/useDefaultOnError';
import {useNavigatePanier} from '@/hooks/panier/useNavigatePanier';
import {useEffect, useState} from 'react';
import {CommandeProblem, LigneProblem} from 'api-types/ffconnect';
import {useProblemParser} from 'ff-common';
import {BlueButton} from '@/components/Button/BlueButton';
import {GrayButton} from '@/components/Button/GrayButton';
import NiceModal from '@ebay/nice-modal-react';
import {ModaleChangerAdresseLivraison} from './PanierRecapitulatif/ModaleChangerAdresseLivraison';
import {useClient} from '@/haligator-factory';
import {ShippingCartIcon} from '@/components/svg/ShippingCartIcon';
import {CheckIcon} from '@chakra-ui/icons';
import {
  PANIER_GUTTER_LEFT,
  PANIER_GUTTER_RIGHT,
} from './PanierFound/panier-layout';
import {usePanierPdf} from '@/hooks/panier/usePanierPdf';
import {VoirPdfButton} from '@/backoffice/domains/PdfOrderImport/VoirPdfButton';
import {PanierPdfAlertes} from './PanierFound/PanierPdfAlertes';
import {FiltreMatching} from './PanierFound/panier-pdf-lignes';

export const PanierFound: React.FC<{
  panier: Navigable<PanierDesc>;
}> = ({panier: panierNav}) => {
  const {t} = useTranslation();
  const {buildClientRoute} = useClientRoute();
  const navigate = useNavigate();
  const textToast = useSimpleTextToast();
  const onError = useDefaultOnError(textToast);
  const navigatePanier = useNavigatePanier();
  const {parseProblemMessage} = useProblemParser();

  // Panier PDF : Chargé une seule fois ici, puis distribué en props aux enfants. Inerte
  // (aucune requête) sur un panier ordinaire ou marketplace.
  const {
    isPanierPdf,
    chargementPdf,
    ordreLignesPdf,
    processus,
    rapportMatch,
    matchingPdf,
  } = usePanierPdf(panierNav);

  // Panier PDF : Etat du filtre dans le header
  const [filtreMatching, setFiltreMatching] = useState<FiltreMatching>('tout');

  const panierHook = useHalState(panierNav);
  const dateCutOff =
    panierHook.state?.data.livraison_tolede.cut_off_de_preparation;

  const panierWithCommands = usePanierCommands(
    panierNav,
    textToast,
    onError,
    navigatePanier,
  );

  const validerOptions = useHalState(
    panierNav.follow('valider_panier_options'),
  );
  const paiementOptionNav = panierNav.follow('paiement_option');
  const validationRefresh = validerOptions.loading || validerOptions.refreshing;
  // Nouveaux states pour les erreurs de ligne et commande
  const [commandeProblems, setCommandeProblems] = useState<CommandeProblem[]>(
    [],
  );
  const [ligneProblems, setLigneProblems] = useState<LigneProblem[]>([]);

  // Mise à jour depuis validerOptions
  useEffect(() => {
    setCommandeProblems(validerOptions.data?.commande_problems ?? []);
    setLigneProblems(validerOptions.data?.lignes_problems ?? []);
  }, [validerOptions.data]);

  // Panier PDF : le filtre appartient au panier affiché.
  // On remet à 'Tout' le filtre lorsque l'on change d'onglet de panier.
  useEffect(() => {
    setFiltreMatching('tout');
  }, [panierHook.data?.id]);

  const bgFade = keyframes({
    from: {opacity: 0.8},
    to: {opacity: 0.5},
  });

  const clientAdressesHook = useHalCollection(
    useClient().follow('adresses_de_livraison'),
  );
  // Affichage du message générique « Vous ne pouvez pas effectuer de commande… » selon
  // la présence ou non du lien 'statut_tolede' :
  // commercial : on n'affiche pas (présence du lien), on filtre les commandeProblems
  // client / assistance : on affiche (absence du lien)
  const clientState = useHalState(useClient());
  const afficheMotifDeBlocage =
    clientState.state?.hasOneLink('statut_tolede') ?? false;
  const problemesAffiches = commandeProblems.filter(
    (problem) => !(problem.code === 'client_bloque' && afficheMotifDeBlocage),
  );
  const changerModeDeRenduOptionHook =
    panierWithCommands.changerModeDeRendu.options;

  const panier = panierHook.data;
  // console.log(validerOptions.error);
  if (!panier) {
    return null;
  }
  const marketplaceId =
    panier.type_panier !== undefined && panier.type_panier !== 'FF'
      ? panier.type_panier
      : undefined;
  const isMarketplace = marketplaceId !== undefined;
  const adresseData = clientAdressesHook.items?.find(
    (adresse) =>
      adresse.data.tolede.code_adresse_de_livraison ===
      panier.livraison_tolede.code_adresse_de_livraison,
  )?.data.tolede;
  const {enseigne} = adresseData
    ? guessTitreAdresseDeLivraison(adresseData)
    : {enseigne: undefined};

  // Déterminer le nombre de colonnes que devraient prendre les lignes de panier
  // Si on n'affiche pas le récapitulatif, les lignes prennent toutes les colonnes
  const contenuColSpan = {base: 1, lg: 6};

  return (
    <Box
      position={{base: 'fixed', md: 'relative'}}
      zIndex={{base: 'overlay', md: 'initial'}}
      minW='100%'
      top='0'
      bottom='0'
      overflowY={{base: 'scroll', lg: 'unset'}}
      overflowX={{base: 'hidden', lg: 'unset'}}
    >
      <VStack
        bg='white'
        py={{base: '0', md: '2'}}
        px={{base: '2', md: '4', lg: '6'}}
        alignItems='start'
        overflow={'hidden'}
      >
        <PanierListe
          isMarketplace={isMarketplace}
          currentPanierId={panier.id}
        />
      </VStack>
      {!isMarketplace && dateCutOff && (
        <HStack
          display={{base: 'flex', md: 'none'}}
          bg='lblue.50'
          justify='center'
          p='1'
          textColor='blue.main'
        >
          {differenceInDays(dateCutOff, new Date()) > 2 ? (
            <Text textAlign='center'>
              {t('panier.validez.plus_de_2_jours')}
            </Text>
          ) : (
            <Text textAlign='center'>
              <Trans t={t}>
                {t('panier.validez.moins_de_2_jours', {
                  date: formatDistanceToNow(dateCutOff),
                })}
              </Trans>
            </Text>
          )}
        </HStack>
      )}

      <Box
        bg='#F5F5F5'
        py={{base: '1', md: '5'}}
        position='relative'
      >
        <Grid
          templateColumns={{base: '1fr', lg: 'repeat(8, 1fr)'}}
          gap={{base: '1', lg: '0'}}
          mb={'10'}
          position='relative'
        >
          <PanierHeader
            viderCommand={panierWithCommands.vider}
            nombre_de_lignes={panier.nombre_de_lignes}
            type_panier={panier.type_panier}
            isViderDisabled={validationRefresh} // Empêche de vider le panier pendant la validation pour éviter les erreurs de race
            isPanierPdf={isPanierPdf}
            chargementPdf={chargementPdf}
            pdfCheminFichier={processus?.data.storage_path}
            nbrLignesMatching={
              matchingPdf ? rapportMatch?.statistiques.nb_lignes : undefined
            }
            filtreMatching={filtreMatching}
            onFiltreMatchingChange={setFiltreMatching}
          />
          {/* Panier PDF : quand le récapitulatif occupe la colonne de droite, le
              bouton "Voir le PDF" vient le coiffer, sur la ligne de l'en-tête du panier. En
              dessous de lg le récapitulatif passe en bas de page : le bouton
              reste alors dans l'en-tête, rendu par PanierHeader. */}
          {isPanierPdf && (
            <GridItem
              display={{base: 'none', lg: 'flex'}}
              justifyContent='flex-end'
              alignItems='center'
              colSpan={{base: 1, lg: 2}}
              // Même respiration sous l'en-tête que PanierHeader, sans quoi le
              // bouton se recentrerait plus bas que le titre du panier.
              pb='10px'
              {...PANIER_GUTTER_RIGHT}
            >
              <VoirPdfButton
                storagePath={processus?.data.storage_path}
                h='35px'
                isDisabled={!processus?.data.storage_path}
              />
            </GridItem>
          )}
          <GridItem
            colSpan={contenuColSpan}
            position='relative'
            {...PANIER_GUTTER_LEFT}
          >
            {/* Si l'adressse doit être confirmée, on affiche un message */}
            {panier.livraison_tolede.code_adresse_de_livraison &&
              panier.validation_adresse_livraison === 'a_faire' && (
                <HStack
                  border='1px solid'
                  borderColor='blue.main'
                  color='blue.main'
                  bg='white'
                  borderRadius='8px'
                  p='3'
                  my='2'
                >
                  <HStack
                    alignItems='start'
                    spacing='3'
                  >
                    <ShippingCartIcon color='blue.main' />
                    <VStack
                      alignItems='start'
                      spacing='0'
                    >
                      <Text fontWeight='600'>
                        {t('panier.confirmer_adresse_livraison.titre')}
                      </Text>
                      {enseigne && <Text>{enseigne}</Text>}
                      <Text fontSize='sm'>
                        {adresseData?.adresse.ligne1}
                        {adresseData?.adresse.ligne2
                          ? `, ${adresseData.adresse.ligne2}`
                          : ''}
                        {` ${adresseData?.adresse.code_postal} ${adresseData?.adresse.ville}`}
                      </Text>
                    </VStack>
                  </HStack>
                  <BlueButton
                    leftIcon={<CheckIcon color='white' />}
                    ml='auto'
                    texte={t(
                      'panier.confirmer_adresse_livraison.bouton_confirmer',
                    )}
                    onClick={() => {
                      panierWithCommands.changerModeDeRendu.submit({
                        code_adresse_de_livraison:
                          panier.livraison_tolede.code_adresse_de_livraison!,
                        mode_de_rendu: panier.livraison_tolede.mode_de_rendu,
                      });
                    }}
                  />
                  <GrayButton
                    texte={t(
                      'panier.confirmer_adresse_livraison.bouton_changer_adresse',
                    )}
                    onClick={() => {
                      // Ouvre la modale
                      NiceModal.show(ModaleChangerAdresseLivraison, {
                        codeAdresse:
                          panier.livraison_tolede.code_adresse_de_livraison!,
                        changerModeDeRenduOptions:
                          changerModeDeRenduOptionHook.state,
                        validateAction:
                          panierWithCommands.changerModeDeRendu.submit,
                        states: clientAdressesHook.items,
                        mode_de_rendu: panier.livraison_tolede.mode_de_rendu,
                      });
                    }}
                  />
                </HStack>
              )}
          </GridItem>

          <GridItem
            colSpan={contenuColSpan}
            position='relative'
            {...PANIER_GUTTER_LEFT}
          >
            {problemesAffiches.length > 0 && (
              <VStack alignItems='stretch'>
                {problemesAffiches.map((problem) => {
                  const problem_message = parseProblemMessage(t, problem);
                  const color =
                    problem.niveau === 'bloquant' ? 'red.warning' : 'blue.dark';
                  const message =
                    problem.code === 'generique' ? (
                      <Tooltip
                        label={t('error.tooltip_generique_details', {
                          message: problem_message,
                          code: problem.code,
                        })}
                        placement='top'
                      >
                        <Text>{t('error.generique')}</Text>
                      </Tooltip>
                    ) : (
                      <Trans
                        t={t}
                        components={{
                          // href provoque un rechargement de la page, on utilise onClick et navigate
                          Contact: (
                            <Link
                              textDecoration='underline'
                              onClick={() => {
                                navigate(
                                  buildClientRoute(routes.client.contacts),
                                );
                              }}
                            />
                          ),
                        }}
                      >
                        {t([`error.${problem.code}`, problem_message])}
                      </Trans>
                    );
                  return (
                    <Box
                      key={ulid()}
                      bg={color}
                      color='white'
                      p='3'
                      my={{base: 0, lg: '2'}}
                    >
                      {message}
                    </Box>
                  );
                })}
              </VStack>
            )}
            {validationRefresh && (
              <Skeleton
                p='2.5'
                mt={{base: '0', md: '4'}}
                mb='2'
                textColor='black.text'
                style={{animation: `1s linear infinite alternate ${bgFade}`}}
                position='relative'
                width='100%'
              >
                {t('panier.nous_validons')}
              </Skeleton>
            )}
            {validerOptions.error &&
              // La commande vider invalide la livraison, le temps que PanierView s'en rende compte, ça évite d'afficher l'erreur
              !isLinkNotFound(validerOptions.error) && (
                <Box
                  bg='red.warning'
                  color='white'
                  p='3'
                  my='2'
                >
                  <Trans t={t}>{t('error.controle_panier')}</Trans>
                </Box>
              )}

            {/* Affichage des alertes ou bloquants liés à la commande PDF */}
            <PanierPdfAlertes exceptions={rapportMatch?.exceptions} />

            <HaliRenderMany
              many={panierNav.followAll('lignes_de_panier')}
              skeleton={() =>
                Array.from(
                  {length: panierHook.data?.nombre_de_lignes ?? 5},
                  (_, idx) => (
                    <Skeleton
                      w='100%'
                      h='8em'
                      mb='2'
                      key={idx}
                    />
                  ),
                )
              }
              render={({states}) => (
                <PanierContent
                  date_de_livraison={
                    panierHook.data!.livraison_tolede.date_de_livraison
                  }
                  lignesDePanier={states}
                  problemesLignes={ligneProblems}
                  validationRefresh={validationRefresh}
                  isMarketplace={isMarketplace}
                  triSelection={panier.tri_selection_panier}
                  isPanierPdf={isPanierPdf}
                  ordreLignesPdf={ordreLignesPdf}
                  matchingPdf={matchingPdf}
                  filtreMatching={filtreMatching}
                />
              )}
            />
          </GridItem>
          <GridItem
            colSpan={{base: 1, lg: 2}}
            {...PANIER_GUTTER_RIGHT}
          >
            <PanierRecapitulatif
              panier={panier}
              panierWithCommands={panierWithCommands}
              validerOptions={validerOptions}
              setCommandeProblems={setCommandeProblems}
              setLigneProblems={setLigneProblems}
              paiementOptionNav={paiementOptionNav}
              rapportMatch={rapportMatch}
              isPanierPdf={isPanierPdf}
            />
          </GridItem>
        </Grid>
      </Box>
    </Box>
  );
};
